import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { AttentionStatus } from '@/services/conversationService';

type Variant = 'default' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';

/**
 * Attention-status → badge-variant mapping. Constrained to the Resend-style
 * monochrome palette (docs/07 §3.2 — no ``--color-info``):
 *
 *   needs_attention → warning  (owner action required; orange/amber draws eye)
 *   active          → default  (bot handling; darkest = actively in progress)
 *   resolved        → success  (closed-positive; green indicates done)
 *   idle            → outline  (faded — no recent activity, low priority)
 */
const STATUS_VARIANT: Record<AttentionStatus, Variant> = {
  needs_attention: 'warning',
  active: 'default',
  resolved: 'success',
  idle: 'outline',
};

interface AttentionStatusBadgeProps {
  status: AttentionStatus;
  className?: string;
  'data-testid'?: string;
}

export function AttentionStatusBadge({
  status,
  className,
  'data-testid': testId,
}: AttentionStatusBadgeProps) {
  const { t } = useTranslation();
  const variant = STATUS_VARIANT[status];
  return (
    <Badge
      variant={variant}
      className={className}
      data-testid={testId}
      data-variant={variant}
      data-status={status}
    >
      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {t(`conversations.status.${status}`)}
    </Badge>
  );
}
